// UI
import TableHeaderCell from './TableHeaderCell'
import PaginationFooter from '../PaginationFooter'
import TableFilters from './TableFilters'
import TableViews from './TableViews'
import TableColumns from './TableColumns'
import LocationSelect from '../LocationSelect'

// Mantine
import { Tooltip, TextInput, Checkbox, ActionIcon } from '@mantine/core'

// Hooks
import useTable from '@/hooks/useTable'

// React
import { useRef, useState, useEffect } from 'react'

// Next
import { useNavigate } from 'react-router-dom'

// Icons
import { RefreshCw01, SearchLg } from '@untitled-ui/icons-react'

// Row type styles
const rowTypeStyles = {
    grayed: {
        base: 'opacity-50 bg-white odd:bg-gray-50',
        hover: 'hover:bg-gray-100',
    },
    yellow: {
        base: 'bg-yellow-50 odd:bg-yellow-50',
        hover: 'hover:bg-yellow-100',
    },
    default: {
        base: 'bg-white odd:bg-gray-50',
        hover: 'hover:bg-gray-100',
    },
}

// Wrapper
const ItemWrapper = ({ children, href, rowType }) => {
    const navigate = useNavigate()
    const styles = rowTypeStyles[rowType] || rowTypeStyles.default
    const isInteractive = (target) => target.closest('a, button, input, select, textarea, [role="button"], [role="checkbox"]')

    return (
        <div
            className={`table-row relative ${styles.base} ${href ? `${styles.hover} cursor-pointer` : ''}`}
            role={href ? 'link' : undefined}
            tabIndex={href ? 0 : undefined}
            onClick={href ? (event) => {
                if (event.defaultPrevented || isInteractive(event.target)) return
                if (event.metaKey || event.ctrlKey) window.open(href, '_blank', 'noopener')
                else navigate(href)
            } : undefined}
            onKeyDown={href ? (event) => {
                if (event.target === event.currentTarget && event.key === 'Enter') navigate(href)
            } : undefined}
        >
            {children}
        </div>
    )
}

export default function Table({
    availableColumns = [],
    availableFilters = [],
    searchable = false,
    withLocation = false,
    withLocationAll = false,
    engineApiEndpoint,
    defaultSortColumn = 'id',
    defaultSortDirection = 'desc',
    linkItemId,
    linkItemUrl,
    getItemUrl,
    selectKey,
    onlyProductComponentsLocations = false,
    getRowType = null,
    topContent = null,
    className = 'h-[calc(100dvh-98px)] xl:h-[calc(100dvh-46px)]',
}) {
    // Table ref
    const tableRef = useRef()

    // Data
    const {
        tableData,
        count,
        isFetching,
        refetchData,
        changePage,
        tableState,
        columns,
        changeFilter,
        changeFilters,
        query,
        setQuery,
        columnClick,
        resetFilters,
        changeColumns,
        selectView,
        resetView,
        selectedItems,
        toggleItemSelect,
        changeLocationId,
    } = useTable({
        tableRef,
        availableColumns,
        availableFilters,
        engineApiEndpoint,
        defaultSortColumn,
        defaultSortDirection,
        selectKey,
    })

    // Current location id
    const locationId = tableState.location_id ?? '1'

    // Search visibility
    const [isSearchOpened, setIsSearchOpened] = useState(false)
    const isSearchVisible = query.length > 0 || isSearchOpened
    const searchInputRef = useRef()

    useEffect(() => {
        if (query.length > 0) setIsSearchOpened(true)
    }, [query])

    const toggleSearch = () => {
        if (isSearchOpened && query.length === 0) {
            setIsSearchOpened(false)
        } else {
            setIsSearchOpened(true)

            setTimeout(() => {
                searchInputRef.current?.focus()
            }, 100)
        }
    }

    return (
        <>
            <div className={`flex flex-col bg-white ${className}`}>
                {topContent && <div className="max-h-[50%] shrink-0 overflow-y-auto">{topContent}</div>}
                <div className="bg-gray-50 border-b px-5 py-3 flex flex-col gap-2">
                    <div className="flex gap-2 justify-between items-center w-full">
                        {searchable && (
                            <Tooltip label="Rechercher" events={{ hover: true, focus: true, touch: false }}>
                            <ActionIcon
                                size={35}
                                variant="default"
                                aria-label="Rechercher"
                                onClick={toggleSearch}
                            >
                                <SearchLg width={14} height={14} />
                            </ActionIcon>
                            </Tooltip>
                        )}

                        <div className="flex min-w-0 flex-wrap gap-2 justify-between w-full items-center">
                            <div className="min-w-0 flex-1 basis-48">
                                {availableFilters.length > 0 && (
                                    <TableFilters
                                        availableFilters={availableFilters}
                                        tableState={tableState}
                                        changeFilters={changeFilters}
                                        resetFilters={resetFilters}
                                    />
                                )}
                            </div>

                            <div className="flex min-w-0 flex-wrap items-center gap-2 ml-auto">
                                {withLocation && (
                                    <LocationSelect
                                        withAll={withLocationAll}
                                        value={tableState.location_id ?? '1'}
                                        onChange={changeLocationId}
                                        onlyProductComponentsLocations={
                                            onlyProductComponentsLocations
                                        }
                                    />
                                )}

                                <TableViews
                                    tableKey={engineApiEndpoint?.replace(/^\//, '')}
                                    withLocation={withLocation}
                                    currentLocation={locationId}
                                    availableColumns={availableColumns}
                                    availableFilters={availableFilters}
                                    tableState={tableState}
                                    selectView={selectView}
                                />

                                <TableColumns
                                    availableColumns={availableColumns}
                                    tableState={tableState}
                                    changeColumns={changeColumns}
                                />

                                <Tooltip label="Rafraîchir les données" events={{ hover: true, focus: true, touch: false }}>
                                    <ActionIcon
                                        type="button"
                                        size={35}
                                        variant="subtle"
                                        color="gray"
                                        radius="xl"
                                        aria-label="Rafraîchir les données"
                                        onClick={() => refetchData()}
                                    >
                                        <RefreshCw01 width={19} height={19} />
                                    </ActionIcon>
                                </Tooltip>
                            </div>
                        </div>
                    </div>

                    <div className={isSearchVisible ? 'block' : 'hidden'}>
                        <TextInput
                            ref={searchInputRef}
                            clearable
                            placeholder="Rechercher dans le tableau..."
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                        />
                    </div>
                </div>

                <div
                    className="min-h-0 grow overflow-scroll relative overscroll-y-prevent"
                    ref={tableRef}
                >
                    <div className="data-table table table-auto align-middle w-full relative">
                        <div className="table-row w-full sticky top-0 z-10">
                            {false && selectKey && (
                                <div className="table-cell p-5 border-b w-[40px] align-middle">
                                    <Checkbox
                                        onClick={(e) => {
                                            e.stopPropagation()
                                        }}
                                    />
                                </div>
                            )}

                            {columns.map((column) => {
                                return (
                                    <TableHeaderCell
                                        label={column.name}
                                        key={column.key}
                                        tooltip={column.tooltip}
                                        formula={column.formula}
                                        width={column.width}
                                        orderBySlug={
                                            column.sortable ? column.key : null
                                        }
                                        onClick={
                                            column.sortable
                                                ? () => columnClick(column)
                                                : null
                                        }
                                        orderBy={tableState.orderBy}
                                        orderByDirection={
                                            tableState.orderByDirection
                                        }
                                    />
                                )
                            })}
                        </div>

                        {tableData === null && (
                            <div className="table-row-group table-body relative w-full">
                                {Array.from({ length: 25 }).map((_, index) => (
                                    <div className="table-row" key={index}>
                                        {columns.map((column) => (
                                            <div
                                                className="table-cell p-5"
                                                key={column.key}
                                            >
                                                <div className="h-5 w-full bg-gray-100 rounded-lg animate-pulse" />
                                            </div>
                                        ))}
                                    </div>
                                ))}
                            </div>
                        )}

                        {tableData !== null &&
                            tableData.length === 0 &&
                            !isFetching && (
                                <div className="text-gray-500 text-center absolute pt-16 w-full">
                                    Aucun résultat
                                </div>
                            )}

                        <div className="table-row-group h-full table-body relative">
                            {tableData !== null &&
                                tableData.map((item, index) => (
                                    <ItemWrapper
                                        key={index}
                                        href={
                                            getItemUrl ? getItemUrl(item) : linkItemUrl && linkItemId
                                                ? `${linkItemUrl}/${item[linkItemId]}`
                                                : null
                                        }
                                        rowType={
                                            getRowType ? getRowType(item) : null
                                        }
                                    >
                                        {selectKey && (
                                            <div
                                                className="table-cell p-5 border-b w-[40px] align-middle cursor-pointer hover:bg-gray-50"
                                                onClick={() =>
                                                    toggleItemSelect(item)
                                                }
                                            >
                                                <Checkbox
                                                    checked={selectedItems.includes(
                                                        item[selectKey]
                                                    )}
                                                />
                                            </div>
                                        )}

                                        {columns.map((column) => {
                                            if (column.component) {
                                                return (
                                                    <column.component
                                                        key={column.key}
                                                        refetchData={
                                                            refetchData
                                                        }
                                                        current_location_id={parseInt(
                                                            locationId
                                                        )}
                                                        {...item}
                                                    />
                                                )
                                            } else {
                                                return (
                                                    <div
                                                        className="table-cell p-5"
                                                        key={column.key}
                                                    ></div>
                                                )
                                            }
                                        })}
                                    </ItemWrapper>
                                ))}
                        </div>
                    </div>
                </div>

                <div>
                    {true && (
                        <PaginationFooter
                            count={count}
                            page={tableState.page}
                            setPage={changePage}
                            perPage={tableState.perPage}
                            isLoading={isFetching}
                        />
                    )}
                </div>
            </div>
        </>
    )
}
